import { useState } from 'react';

import {
  IconUser,
  IconSchool,
  IconLicense,
  IconCalendar,
  IconCircleCheck,
} from '@tabler/icons-react';

import { Breadcrumb } from '@/components/BreadCrumbs';
import CustomModal from '@/components/CustomModal';
import DatePicker from '@/components/DatePicker';
import { SegmentedControl } from '@/components/SegmentedControl';
import Select from '@/components/Select';
import { Switch } from '@/components/Switch';
import { useFormatText } from '@/hooks/useFormatText';

interface IRenewLicenseModalProps {
  setIsOpen: (open: boolean) => void;
  id: string | null;
  type?: 'parents' | 'schools';
  onBack?: () => void;
}

const RenewLicenseModal = ({
  setIsOpen,
  id,
  type = 'parents',
  onBack,
}: IRenewLicenseModalProps) => {
  console.log('Renewing for:', id);
  const { formatText } = useFormatText();
  const [renewalType, setRenewalType] = useState('auto');
  const [renewalDate, setRenewalDate] = useState<string | null>('2025-12-15');
  const [planType, setPlanType] = useState('premium');
  const [sendReminder, setSendReminder] = useState(true);

  const isSchool = type === 'schools';

  // Mock data for context
  const name = isSchool ? 'Bright Minds School' : 'Fatima Hassan';
  const licenseTypeLabel = isSchool ? 'Enterprises' : 'Parent';
  const currentExpiry = 'Dec 31, 2025';

  const planOptions = [
    { value: 'basic', label: 'Basic' },
    { value: 'premium', label: 'Premium' },
    { value: 'standard', label: 'Standard' },
  ];

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      headercontent={
        <Breadcrumb
          path={[
            {
              label: formatText(
                isSchool
                  ? 'report-analytics.license.school-details.title'
                  : 'report-analytics.license.parent-details.title'
              ),
              href: 'details',
            },
            {
              label: formatText('report-analytics.license.renew-license.title'),
              href: 'renew',
            },
          ]}
          onNavigateRequest={(href) => {
            if (href === 'details') onBack?.();
          }}
        />
      }
      position="right"
      className="max-w-150"
      isFooter
      confirmButtonProps={{
        label: formatText(
          'report-analytics.license.renew-license.schedule-renewal'
        ),
        onClick: () => setIsOpen(false),
        className:
          'bg-purple-600 hover:bg-purple-700 h-11 px-8 rounded-xl font-bold',
      }}
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        onClick: () => setIsOpen(false),
        variant: 'link',
        className: 'text-neutral-primary! font-medium',
      }}
    >
      <div className="flex flex-col gap-10 w-full overflow-x-hidden">
        {/* License Details Section */}
        <div className="space-y-6">
          <h4 className="text-lg font-semibold text-neutral-primary font-mona-sans">
            {formatText(
              'report-analytics.license.renew-license.license-details'
            )}
          </h4>
          <div className="grid grid-cols-2 gap-y-6 gap-x-8">
            <div className="flex items-start gap-3">
              <div className="text-neutral-400">
                {isSchool ? <IconSchool size={20} /> : <IconUser size={20} />}
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary font-medium">
                  {formatText(isSchool ? 'sidebar.school' : 'common.name')}
                </span>
                <span className="text-base font-semibold text-neutral-primary font-mona-sans">
                  {name}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-neutral-400">
                <IconLicense size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary font-medium">
                  {formatText(
                    'report-analytics.license.renew-license.license-type'
                  )}
                </span>
                <span className="text-base font-semibold text-neutral-primary font-mona-sans">
                  {licenseTypeLabel}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-neutral-400">
                <IconCalendar size={18} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary font-medium">
                  {formatText('report-analytics.license.table.expiry-date')}
                </span>
                <span className="text-base font-semibold text-neutral-primary font-mona-sans">
                  {currentExpiry}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="text-neutral-400">
                <IconCircleCheck size={18} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary font-medium">
                  {formatText('report-analytics.license.table.status')}
                </span>
                <div className="mt-1">
                  <span className="px-3 py-1 bg-warning-light-50 text-orange-400 rounded-full text-xs font-semibold font-mona-sans">
                    {formatText('common.status.expiring-soon')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Renewal Configuration Section */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-neutral-primary font-mona-sans">
            {formatText(
              'report-analytics.license.renew-license.renewal-config'
            )}
          </h4>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-semibold text-neutral-primary">
                {formatText(
                  'report-analytics.license.renew-license.renewal-type'
                )}
              </span>
              <SegmentedControl
                value={renewalType}
                onChange={setRenewalType}
                fullWidth
                options={[
                  {
                    label: formatText(
                      'report-analytics.license.renew-license.auto-renewal'
                    ),
                    value: 'auto',
                  },
                  {
                    label: formatText(
                      'report-analytics.license.renew-license.manual-renewal'
                    ),
                    value: 'manual',
                  },
                ]}
                className="bg-neutral-light-50 p-1 rounded-xl h-12"
              />
            </div>

            <div className="flex flex-col gap-2 font-mona-sans">
              <span className="text-sm font-medium text-neutral-primary">
                {formatText(
                  'report-analytics.license.renew-license.renewal-date'
                )}
              </span>
              <DatePicker value={renewalDate} onChange={setRenewalDate} />
            </div>
            <Select
              name="planType"
              options={planOptions}
              selected={planType}
              onChange={(val) => setPlanType(val as string)}
              className="h-12 rounded-xl !text-neutral-primary!"
            />
          </div>
        </div>

        {/* Automated Actions */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-neutral-primary font-mona-sans">
            {formatText(
              'report-analytics.license.renew-license.automated-actions'
            )}
          </h4>
          <div className="flex items-center justify-between font-mona-sans">
            <span className="text-base text-neutral-secondary font-medium">
              {formatText(
                isSchool
                  ? 'report-analytics.license.renew-license.send-reminder-school'
                  : 'report-analytics.license.renew-license.send-reminder'
              )}
            </span>
            <Switch checked={sendReminder} onChange={setSendReminder} />
          </div>
        </div>

        {/* Renewal Summary */}
        <div className="space-y-4 font-mona-sans">
          <h4 className="text-lg font-semibold text-neutral-primary border-b border-neutral-light-200 pb-2">
            {formatText(
              'report-analytics.license.renew-license.renewal-summary'
            )}
          </h4>
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm font-medium">
              <span className="text-neutral-secondary">
                {formatText(
                  'report-analytics.license.renew-license.renewal-date'
                )}
              </span>
              <span className="text-neutral-primary">Dec 15, 2025</span>
            </div>
            <div className="flex justify-between items-center text-sm font-medium">
              <span className="text-neutral-secondary">
                {formatText('report-analytics.license.renew-license.plan')}
              </span>
              <span className="text-neutral-primary">Premium</span>
            </div>
            <div className="flex justify-between items-center text-lg py-2 border-t border-purple-disabled">
              <span className="font-semibold text-neutral-primary">
                {formatText(
                  'report-analytics.license.renew-license.total-cost'
                )}
              </span>
              <span className="font-bold text-neutral-primary">$3,754</span>
            </div>
            <div className="flex justify-between items-center text-sm pt-4 border-t border-neutral-light-200">
              <span className="text-neutral-secondary font-medium">
                {formatText(
                  'report-analytics.license.renew-license.new-expiry'
                )}
              </span>
              <span className="text-success-secondary font-bold">
                Dec 15, 2026
              </span>
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default RenewLicenseModal;
